// ./src/components/features/Inventory/InventoryItemSelect/InventoryItemSelect.tsx
//
// Picks a catalog inventory item for a stock operation / mapping. SERVER-SEARCH-backed:
// typing runs an account-scoped (event-scoped for crew) `searchItems` lookup via the
// InventoryItemsContext, so items beyond the loaded first page are findable (the plain
// provider list only holds one page). Before the user types, the dropdown shows the
// provider's already-loaded items. Also lets the user create one in place. On pick it
// hands back the whole item so the console can seed the unit / currency.
//
// ⚠ THE CREATE-IN-PLACE IS PLAN-GATED, AND THE GATE LIVES HERE RATHER THAN ON THE PAGES.
// `InventoryItemCore.beforeCreate` refuses without `production`, but the things the hosting
// screens actually create — a stock TRANSACTION, a tally row, a voucher line — are not gated
// at all. So those pages must NOT carry a page-level notice; it is only this one action
// inside them that the server refuses. This picker appears in four places (the ops console,
// the purchase-order lines editor, the tally items editor and the voucher items editor), and
// only one of their pages happens to gate `production` — gating it here covers all four and
// cannot be forgotten by the fifth.
//
// ⚠ IT DOES NOT NAVIGATE TO THE UPGRADE PAGE. The picker sits inside a half-filled drawer or
// console, and sending the host to Subscription from there would throw away what they had
// entered. The action is withdrawn and the picker EXPLAINS itself, in the server's own words.

import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Loader } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { FEATURE_CODES } from "@/config";
import { InventoryItem } from "@/utils/api";
import { useInventoryItemsContext } from "@/contexts";
import { useCreateGate } from "@/hooks/useCreateGate";
import { useCreateInPlace } from "@/hooks/useCreateInPlace";
import { useCurrentEvent } from "@/contexts/CurrentEventContext/CurrentEventContext";
import { CREATE_FOR_EVENT_OPERATORS } from "@/utils/createInPlace";
import { AppIcon, SelectField } from "@/components/generic";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";

import { InventoryItemEditDrawer, InventoryItemPrefill } from "../InventoryItemEditDrawer/InventoryItemEditDrawer";

export interface InventoryItemSelectProps {
  value: string | null;
  onChange: (item: InventoryItem | null) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  error?: React.ReactNode;
  /** Event context for crew (account-scoped ops) — handed to the create-in-place drawer. */
  contextEventId?: string | null;
  /**
   * Seed for the create-in-place drawer — e.g. the unknown code the operator just scanned, so an
   * item created from HERE carries that barcode too, exactly as one created from the scan banner does.
   */
  createPrefill?: InventoryItemPrefill | null;
  /**
   * Told when the create-in-place drawer opens or closes — a scan console pauses its camera while
   * it is open, or a code caught behind the drawer would change the line being created for.
   */
  onCreateDrawerChange?: (open: boolean) => void;
}

const SEARCH_DEBOUNCE_MS = 250;

export const InventoryItemSelect: React.FC<InventoryItemSelectProps> = ({
  value,
  onChange,
  label,
  description,
  disabled,
  error,
  contextEventId,
  createPrefill,
  onCreateDrawerChange,
}) => {
  const { t } = useTranslation(["inventory", "common"]);
  // ⚠ The SAME feature the catalog page gates on — `InventoryItemCore` checks `production` in
  // `beforeCreate`, so this is the one resolver answering the one question, not a second rule.
  // #142 — the event the item is created under (the provider's, else the workspace's): its account's plan decides, and
  // a crew member may only with `event.operate`.
  const { currentEventId } = useCurrentEvent();
  const gateEventId = contextEventId ?? currentEventId ?? null;
  const createGate = useCreateGate(FEATURE_CODES.PRODUCTION, { eventId: gateEventId });
  const canCreate = useCreateInPlace(CREATE_FOR_EVENT_OPERATORS, FEATURE_CODES.PRODUCTION, gateEventId);
  const { inventoryItems, loading, searchItems, get } = useInventoryItemsContext();
  const [drawerOpen, setDrawerOpenState] = useState(false);
  const onCreateDrawerChangeRef = useRef(onCreateDrawerChange);
  onCreateDrawerChangeRef.current = onCreateDrawerChange;
  const setDrawerOpen = useCallback((open: boolean) => {
    setDrawerOpenState(open);
    onCreateDrawerChangeRef.current?.(open);
  }, []);

  // Controlled search → server lookup (debounced). Empty query falls back to the loaded list.
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<InventoryItem[]>([]);
  const [searching, setSearching] = useState(false);
  // The selected item resolved by id (for an existing mapping whose item isn't in the loaded page).
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const q = query.trim();

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!q) {
      setResults([]);
      setSearching(false);

      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      const rows = await searchItems(q);
      setResults(rows);
      setSearching(false);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchItems]);

  // Resolve the selected value's real label when it isn't among the loaded / searched items.
  useEffect(() => {
    if (!value) {
      setSelectedItem(null);

      return;
    }

    const known = inventoryItems.find((it) => it.id === value) || results.find((it) => it.id === value);
    if (known) {
      setSelectedItem(known);

      return;
    }

    let cancelled = false;
    get(value).then((result) => {
      if (!cancelled && result.didSucceed()) {
        setSelectedItem(result.getDataFirst() as InventoryItem);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [value, inventoryItems, results, get]);

  const data = useMemo(() => {
    const base = query.trim() ? results : inventoryItems;
    const opts = base
      .filter((it) => !!it.id)
      .map((it) => ({
        value: it.id as string,
        label: it.name || t("inventory:item.untitled", { defaultValue: "Untitled item" }),
      }));

    // Keep the current selection selectable + correctly labelled even when it isn't in the list.
    if (value && !opts.some((o) => o.value === value)) {
      opts.unshift({ value, label: selectedItem?.name || t("inventory:item.selected", { defaultValue: "Selected item" }) });
    }

    return opts;
  }, [query, results, inventoryItems, value, selectedItem, t]);

  const handleChange = useCallback(
    (id: string | null) => {
      // Mantine drives `searchValue` (our query) to the picked option's label on select and to "" on
      // clear, so the input reflects the selection — don't reset it here (that fights the component).
      if (!id) {
        onChange(null);

        return;
      }

      const item = results.find((it) => it.id === id) || inventoryItems.find((it) => it.id === id) || (selectedItem?.id === id ? selectedItem : null);
      onChange(item ?? null);
    },
    [results, inventoryItems, selectedItem, onChange]
  );

  const handleCreated = (item: InventoryItem) => {
    setDrawerOpen(false);
    onChange(item);
    // Create bypasses the Select, so reflect the new item's name in the (controlled) input ourselves.
    setQuery(item.name ?? "");
  };

  return (
    <>
      <SelectField
        label={label ?? t("inventory:item.label", { defaultValue: "Item" })}
        description={description}
        placeholder={
          loading
            ? t("common:loading", { defaultValue: "Loading…" })
            : t("inventory:item.placeholder", { defaultValue: "Pick an inventory item" })
        }
        data={data}
        value={value}
        onChange={(_n, id) => handleChange(id)}
        searchable
        searchValue={query}
        onSearchChange={setQuery}
        filter={({ options }) => options}
        searchLoading={searching}
        createLabel={
          canCreate
            ? t("inventory:item.create", { defaultValue: "Add a new inventory item" })
            : undefined
        }
        onCreate={canCreate ? () => setDrawerOpen(true) : undefined}
        clearable
        disabled={disabled}
        error={error}
        nothingFoundMessage={
          searching
            ? t("common:loading", { defaultValue: "Loading…" })
            : // ⚠ THE REFUSAL IS SAID, NOT JUST OBEYED — an empty picker with the only way to
              // fill it withdrawn and no reason given is indistinguishable from a bug.
              createGate.blockedTooltip ||
              t("inventory:item.empty", { defaultValue: "No items yet — add one" })
        }
        leftSection={<AppIcon iconName="Package" size={16} />}
        rightSection={searching || loading ? <Loader size="md" /> : undefined}
        comboboxProps={DRAWER_COMBOBOX_PROPS}
        size="md"
      />

      <InventoryItemEditDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={handleCreated}
        contextEventId={contextEventId}
        prefill={createPrefill}
      />
    </>
  );
};

export default InventoryItemSelect;
